import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNotes } from '../../contexts/NotesContext';
import { useAuth } from '../../contexts/AuthContext';
import { notesApi } from '../../services/cloudApi';

/**
 * Modal to pick a note to share into the current chat (DM or group).
 * Uses existing notesApi.share; no new logic.
 */
const NoteListModal = ({ isOpen, onClose, shareTarget, onShareSuccess }) => {
  const { t } = useTranslation('chat');
  const { notes } = useNotes();
  const { authToken } = useAuth();
  const [isSharing, setIsSharing] = useState(false);

  const handleSelectNote = async (note) => {
    if (!authToken || !shareTarget || !note?.id) return;
    const { type } = shareTarget;
    const receiverIds = type === 'friend' ? [shareTarget.friendEmail] : [];
    const groupId = type === 'group' ? shareTarget.groupId : null;

    setIsSharing(true);
    try {
      await notesApi.share({
        token: authToken,
        noteId: note.id,
        receiverIds,
        groupId,
      });
      if (typeof onShareSuccess === 'function') {
        onShareSuccess();
      }
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: { type: 'success', title: t('common:success'), message: t('noteListModal.shareSuccessMessage') },
        })
      );
      onClose();
    } catch (error) {
      console.error('Share note to chat error:', error);
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: { type: 'error', title: t('common:errorTitle'), message: t('noteListModal.shareErrorMessage') },
        })
      );
    } finally {
      setIsSharing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="note-list-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/50 backdrop-blur-md z-[10000]"
      />
      <motion.div
        key="note-list-modal"
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="fixed inset-0 flex items-center justify-center p-4 z-[10001]"
        onClick={onClose}
      >
        <div
          className="bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-2xl w-full max-w-md border border-white/10 dark:border-white/10 max-h-[70vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-white/10 dark:border-white/10">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] dark:text-white">{t('noteListModal.title')}</h3>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {!notes?.length ? (
              <p className="p-4 text-sm text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] text-center">{t('noteListModal.noNotes')}</p>
            ) : (
              notes.map((note, index) => {
                const title = note.title?.trim() || t('common:unnamed');
                const snippet = note.content
                  ? note.content.replace(/<[^>]*>/g, '').trim().substring(0, 80)
                  : '';
                return (
                  <button
                    key={note.id ?? `note-${index}`}
                    type="button"
                    onClick={() => handleSelectNote(note)}
                    disabled={isSharing}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] transition-colors text-left disabled:opacity-50"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#2563eb]/10 dark:bg-[#2563eb]/10 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-[#3d8ef0] dark:text-[#7ab8f8]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--color-text-primary)] dark:text-white truncate">{title}</p>
                      {snippet && (
                        <p className="text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] truncate mt-0.5">{snippet}</p>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NoteListModal;
